/* eslint-disable @scandipwa/scandipwa-guidelines/jsx-no-conditional */
/* eslint-disable max-lines */
/* eslint-disable max-len */
/* eslint-disable react/no-unknown-property */
/* eslint-disable @scandipwa/scandipwa-guidelines/only-render-in-component */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import { PropTypes } from 'prop-types';

import Image from 'Component/Image';
import Link from 'Component/Link';
import Loader from 'Component/Loader';
import MyAccountAddressTable from 'Component/MyAccountAddressTable';
import OrderSuccessCarousel from 'Component/OrderSuccessCarousel';
import {
    BILLING_ADDRESS_KEY,
    PAYMENT_METHOD_CODE_KEY,
    PAYMENT_METHOD_KEY,
    RECOMMENDER_CHECKOUT_PAGE_ID,
    SHIPPING_ADDRESS_KEY
} from 'Route/Checkout/Checkout.config';
import {
    CheckoutSuccess as SourceCheckoutSuccess
} from 'SourceComponent/CheckoutSuccess/CheckoutSuccess.component';
import { CART_TOTALS } from 'Store/Cart/Cart.reducer';
import { CUSTOMER } from 'Store/MyAccount/MyAccount.dispatcher';
import { isArabic } from 'Util/Arabic';
import BrowserDatabase from 'Util/BrowserDatabase/BrowserDatabase';
import { formatPrice } from 'Util/Price';

import { GUEST_EMAIL } from './CheckoutSuccess.config';

import './CheckoutSuccess.extended.style';

/** @namespace Scandipwa/Component/CheckoutSuccess/Component */
export class CheckoutSuccessComponent extends SourceCheckoutSuccess {
    static propTypes = {
        ...super.propTypes,
        isLoading: PropTypes.bool.isRequired,
        storeName: PropTypes.string.isRequired,
        locale: PropTypes.string.isRequired
    };

    // So plugin can attach to something
    componentDidMount() {}

    renderAddress(address, title) {
        if (!address) {
            return null;
        }

        const formattedAddress = { ...address };

        if (!formattedAddress.region && formattedAddress.region_id) {
            formattedAddress.region = { region_id: address.region_id };
        }

        if (!formattedAddress.region && !formattedAddress.region_id) {
            return null;
        }

        return (
            <div block="CheckoutSuccess" elem="ShippingAddress">
                <h3>{ title }</h3>
                <MyAccountAddressTable
                  address={ formattedAddress }
                  showAdditionalFields
                  mix={ { block: 'CheckoutSuccess', elem: 'ShippingAddressTable' } }
                />
            </div>
        );
    }

    renderButtons() {
        return (
            <div block="CheckoutSuccess" elem="Buttons">
                <div block="CheckoutSuccess" elem="ButtonWrapper">
                    <Link
                      block="Button"
                      elem="Checkout"
                      mods={ { withArrow: true } }
                      to="/"
                    >
                        { __('Continue shopping') }
                    </Link>
                </div>
                { this.renderCreateAccountButton() }
            </div>
        );
    }

    renderCreateAccountButton() {
        const { isSignedIn = false } = this.props;

        const accountButtonsText = isSignedIn ? __('My account') : __('Create an account');

        return (
            <div
              block="CheckoutSuccess"
              elem="ButtonWrapper"
              mods={ { isCreateAccountButton: true } }
            >
                <Link
                  block="Button"
                  elem="Checkout"
                  mods={ { withArrow: true } }
                  to="/customer/account/"
                >
                    { accountButtonsText }
                </Link>
            </div>
        );
    }

    renderThankYou() {
        const customerEmail = BrowserDatabase.getItem(CUSTOMER)?.email || BrowserDatabase.getItem(GUEST_EMAIL);

        return (<p>{ __('Thank you for shopping with us. Your order has been received and you will shortly receive a confirmation email to %s', customerEmail) }</p>);
    }

    renderDate() {
        const today = new Date();

        return (<span>{ `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}` }</span>);
    }

    renderDeliveryMethod(cartTotals) {
        return (<span>{ cartTotals?.shipping_addresses?.selected_shipping_method?.carrier_title }</span>);
    }

    renderItemsTable(cartTotals) {
        return (
            <table block="CheckoutSuccess" elem="OrderItems">
                <thead>
                    { this.renderHeadingRow() }
                </thead>
                <tbody>
                    { this.renderRows(cartTotals) }
                </tbody>
            </table>
        );
    }

    renderHeadingRow() {
        return (
            <tr>
                <th><h1>{ __('Order Summary') }</h1></th>
                <th>{ __('Quantity') }</th>
                <th>{ __('Price') }</th>
                <th>{ __('Total') }</th>
            </tr>
        );
    }

    renderRows(cartTotals) {
        return cartTotals?.items.reduceRight(
            (acc, e) => [...acc, this.renderRow(e)],
            []
        );
    }

    renderRow(item) {
        const { quantity, configurable_options, product: { name, variants = [] } = {} } = item;

        if (variants.length === 0) {
            return <tr />;
        }

        const currency = variants[0]?.price_range?.minimum_price?.final_price?.currency;
        const price = variants[0]?.price_range?.minimum_price?.final_price?.value;

        return (
            <tr block="CheckoutSuccess" elem="OrderItem">
                <td block="CheckoutSuccess" elem="ItemDetails">
                    <Image
                      src={ variants[0]?.thumbnail?.url }
                      ratio="custom"
                      alt="Order Item thumbnail"
                    />
                    <div block="CheckoutSuccess" elem="ItemDetailsText">
                        <span>{ name }</span>
                        { configurable_options.map(({ option_label, value_label }) => (
                            <p>{ `${option_label }: ${value_label}` }</p>
                        )) }
                    </div>
                </td>
                <td>{ quantity }</td>
                <td>{ formatPrice(price, currency) }</td>
                <td>{ formatPrice(price * quantity, currency) }</td>
            </tr>
        );
    }

    render() {
        const {
            orderID, isLoading, cash_on_delivery_fee
        } = this.props;

        if (isLoading) {
            return <Loader isLoading={ isLoading } />;
        }

        const cartTotals = BrowserDatabase.getItem(CART_TOTALS);

        return (
            <div block="CheckoutSuccess">
                { this.renderThankYou() }
                <p block="CheckoutSuccess" elem="Line">
                { isArabic()
                    ? (
                        <>
                            <span>{ orderID }</span>
                            { __('Order Number: ') }
                        </>
                    )
                    : (
                        <>
                            { __('Order Number: ') }
                            <span>{ orderID }</span>
                        </>
                    ) }
                </p>
                <p block="CheckoutSuccess" elem="Line">
                    { isArabic()
                        ? (
                            <>
                                { this.renderDate() }
                                { __('Order Date: ') }
                            </>
                        )
                        : (
                            <>
                                { __('Order Date: ') }
                                { this.renderDate() }
                            </>
                        ) }
                </p>
                <p block="CheckoutSuccess" elem="Line">
                    { isArabic()
                        ? (
                            <>
                                { this.renderDeliveryMethod(cartTotals) }
                                { __('Delivery method: ') }
                            </>
                        )
                        : (
                            <>
                                { __('Delivery method: ') }
                                { this.renderDeliveryMethod(cartTotals) }
                            </>
                        ) }
                </p>
                { this.renderItemsTable(cartTotals) }
                <div block="CheckoutSuccess" elem="Info">
                    <div block="CheckoutSuccess" elem="InfoColOne">
                        <h1 block="CheckoutSuccess" elem="InfoColTitle">{ __('Delivery Information') }</h1>
                        <div block="CheckoutSuccess" elem="InfoColSection">
                            { this.renderAddress(BrowserDatabase.getItem(BILLING_ADDRESS_KEY), __('Billing Address')) }
                            { this.renderAddress(BrowserDatabase.getItem(SHIPPING_ADDRESS_KEY), __('Delivery Address')) }
                        </div>
                    </div>
                    <div block="CheckoutSuccess" elem="InfoColTwo">
                        <h1 block="CheckoutSuccess" elem="InfoColTitle">{ __('Payment Method') }</h1>
                        <p block="CheckoutSuccess" elem="Line">{ BrowserDatabase.getItem(PAYMENT_METHOD_KEY) ?? '' }</p>
                        <h1 block="CheckoutSuccess" elem="InfoColTitle">{ __('Billing Summary') }</h1>
                        <div block="CheckoutSuccess" elem="ItemTotals">
                            <span block="CheckoutSuccess" elem="Line">{ __('Items Total') }</span>
                            <span block="CheckoutSuccess" elem="Line">{ `${cartTotals?.prices?.grand_total?.currency } ${ cartTotals?.prices?.grand_total?.value}` }</span>
                        </div>
                        { BrowserDatabase.getItem(PAYMENT_METHOD_CODE_KEY) === 'cashondelivery' && cash_on_delivery_fee > 0 && (
                            <div block="CheckoutSuccess" elem="CashOnDeliveryFee">
                                <span block="CheckoutSuccess" elem="Line">{ __('Cash on Delivery Fee') }</span>
                                <span block="CheckoutSuccess" elem="Line">{ `${cartTotals?.prices?.grand_total?.currency } ${ cash_on_delivery_fee}` }</span>
                            </div>
                        ) }
                        <div block="CheckoutSuccess" elem="Shipping">
                            <span block="CheckoutSuccess" elem="Line">{ __('Shipping') }</span>
                            <span block="CheckoutSuccess" elem="Line">{ `${cartTotals?.prices?.grand_total?.currency } ${ cartTotals?.prices?.shipping_incl_tax}` }</span>
                        </div>
                        <div block="CheckoutSuccess" elem="OrderTotals">
                            <span block="CheckoutSuccess" elem="Line">{ __('Total For Your Order') }</span>
                            <span block="CheckoutSuccess" elem="Line">{ `${cartTotals?.prices?.grand_total?.currency } ${ cartTotals?.prices?.grand_total?.value + (BrowserDatabase.getItem(PAYMENT_METHOD_CODE_KEY) === 'cashondelivery' && cash_on_delivery_fee > 0 ? cash_on_delivery_fee : 0)}` }</span>
                        </div>
                    </div>
                </div>
                <div id={ RECOMMENDER_CHECKOUT_PAGE_ID }>
                    <OrderSuccessCarousel />
                </div>
            </div>
        );
    }
}

export default CheckoutSuccessComponent;
